import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { TranslocoService } from '@ngneat/transloco';

import { ZardButtonComponent } from '@solvace/zard/shared/components/button';
import { ZardDialogRef } from '@solvace/zard/shared/components/dialog/dialog-ref';
import { Z_MODAL_DATA } from '@solvace/zard/shared/components/dialog/dialog.service';
import { SOLVACE_ICONS } from '@solvace/zard/svz-icons/solvace-icons';

import { LibSharedCommonModule } from 'projects/lib-shared-common/src/public-api';

export type SvzEmbedCodeDialogVariant = 'default' | 'sds-dialog';

export interface SvzEmbedCodeDialogData {
  /** The snippet shown in the code block and copied to the clipboard. */
  code: string;
  title?: string;
  description?: string;
  copyText?: string;
  closeText?: string;
  /** Draws the ✕ button next to the title (`.dialog-head` of the RCA prototype `SDS.dialog`). Off by default: the Alerts `anEmbedModal` has none. */
  closeIcon?: boolean;
  /**
   * `sds-dialog` draws the RCA prototype `SDS.dialog` geometry (RCA/index.html 2099-2103): head `18px 20px`, description
   * `0 20px 4px` in `#5a5f6b`, actions `18px 20px` with `btn-sm` buttons (34px, `0 12px`, 13px). Default keeps the Alerts `anEmbedModal`.
   */
  variant?: SvzEmbedCodeDialogVariant;
  /** Screen prefix for the `svcTestId`s (`<prefix>__btn--embed-copy`, `<prefix>__btn--embed-close`, `<prefix>__text--embed-code`). */
  testIdPrefix?: string;
}

/**
 * The prototype "Embed this chart" dialog (`anEmbedModal`, Alerts/index.html 7651-7663): the design-system
 * `#sds-dialog` (radius 14, 22px box, 18/700 title, 14px `#717182` description) with the snippet in a
 * `.an-embed-code` block and two actions, Close and Copy code. Closes with `true` after copying, `false` otherwise.
 */
@Component({
  selector: 'svz-embed-code-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIcon, ZardButtonComponent, LibSharedCommonModule],
  providers: [provideIcons({ svcX: SOLVACE_ICONS.svcX })],
  templateUrl: './svz-embed-code-dialog.component.html',
})
export class SvzEmbedCodeDialogComponent {
  private readonly dialogRef = inject(ZardDialogRef<SvzEmbedCodeDialogComponent, boolean>);
  private readonly data = inject<SvzEmbedCodeDialogData>(Z_MODAL_DATA);
  private readonly translocoService = inject(TranslocoService);

  protected readonly code = this.data?.code ?? '';
  protected readonly title = this.data?.title ?? this.translocoService.translate('Incorporar este gráfico');
  protected readonly description =
    this.data?.description ?? this.translocoService.translate('Cole este trecho onde o gráfico deve aparecer. A visualização incorporada segue os filtros do link.');
  protected readonly copyText = this.data?.copyText ?? this.translocoService.translate('Copiar código');
  protected readonly closeText = this.data?.closeText ?? this.translocoService.translate('Fechar');
  protected readonly closeIcon = this.data?.closeIcon ?? false;
  protected readonly testIdPrefix = this.data?.testIdPrefix ?? 'svz-embed-code-dialog';
  protected readonly isSdsDialog = this.data?.variant === 'sds-dialog';
  protected readonly headClass = this.isSdsDialog
    ? 'mb-2 flex items-center justify-between gap-3 px-5 py-[18px]'
    : 'mb-2 flex items-center justify-between gap-3';
  protected readonly bodyClass = this.isSdsDialog ? 'px-5 pb-1' : '';
  protected readonly descriptionClass = this.isSdsDialog
    ? 'mb-2.5 text-[14px] leading-[1.5] text-[#5a5f6b]'
    : 'mb-2.5 text-[14px] leading-[1.5] text-[#717182]';
  protected readonly codeClass = this.isSdsDialog
    ? 'block break-all rounded-[8px] border border-input bg-muted px-3 py-2.5 text-left font-mono text-xs leading-[1.5] text-foreground'
    : 'mb-5 block break-all rounded-[8px] border border-input bg-muted px-3 py-2.5 text-left font-mono text-xs leading-[1.5] text-foreground';
  protected readonly actionsClass = this.isSdsDialog ? 'flex flex-row justify-end gap-[10px] px-5 py-[18px]' : 'flex flex-row justify-end gap-[10px]';
  protected readonly actionButtonClass = this.isSdsDialog
    ? 'h-[34px] rounded-[8px] px-3 text-[13px] font-semibold'
    : 'h-[38px] rounded-[10px] px-4 text-sm font-semibold';

  protected async copy(): Promise<void> {
    try {
      await globalThis.navigator?.clipboard?.writeText(this.code);
    } catch {
      // The clipboard can be unavailable (insecure context, denied permission); the snippet stays visible to select by hand.
    }
    this.dialogRef.close(true);
  }

  protected close(): void {
    this.dialogRef.close(false);
  }
}
